import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  Plus, 
  Upload, 
  Clock, 
  User, 
  Truck,
  Calendar,
  Search,
  RefreshCw,
  Filter,
  Download,
  Eye,
  Trash2,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api',
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const DocketsPage = () => {
  const [dockets, setDockets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterJob, setFilterJob] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [selectedDocket, setSelectedDocket] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docket_number: '',
    job_id: '',
    employee_id: '',
    employee_name: '',
    equipment_type: '',
    equipment_registration: '',
    date: '',
    actual_start_time: '07:00',
    actual_end_time: '15:30',
    actual_break_minutes: 30,
    work_description: '',
    supervisor_name: '',
    notes: ''
  });

  useEffect(() => {
    fetchDockets();
    fetchJobs();
  }, []);

  const fetchDockets = async () => {
    setLoading(true);
    try {
      const response = await API.get('/dockets');
      setDockets(response.data);
    } catch (error) {
      toast.error('Failed to load dockets');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await API.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to load jobs');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await API.post('/dockets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.dockets_created} dockets`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload dockets');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreateDocket = async () => {
    if (!formData.docket_number || !formData.employee_id || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await API.post('/dockets', formData);
      toast.success('Docket created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create docket');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (docketId) => {
    try {
      await API.delete(`/dockets/${docketId}`);
      toast.success('Docket deleted');
      fetchDockets();
    } catch (error) {
      toast.error('Failed to delete docket');
    }
  };

  const resetForm = () => {
    setFormData({
      docket_number: '',
      job_id: '',
      employee_id: '',
      employee_name: '',
      equipment_type: '',
      equipment_registration: '',
      date: '',
      actual_start_time: '07:00',
      actual_end_time: '15:30',
      actual_break_minutes: 30,
      work_description: '',
      supervisor_name: '',
      notes: ''
    });
  };

  const calculateHours = (start, end, breakMins) => {
    try {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      const totalMins = (eh * 60 + em) - (sh * 60 + sm) - breakMins;
      return (totalMins / 60).toFixed(1);
    } catch {
      return '0.0';
    }
  };

  const filteredDockets = dockets.filter(docket => {
    const matchesSearch = docket.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          docket.docket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          docket.equipment_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesJob = filterJob === 'all' || docket.job_id === filterJob;
    const matchesDate = !filterDate || docket.date === filterDate;
    return matchesSearch && matchesJob && matchesDate;
  });

  const getSourceBadge = (source) => {
    switch (source) {
      case 'equipr': return <Badge className="bg-blue-100 text-blue-700">EQUIPR</Badge>;
      case 'csv_upload': return <Badge className="bg-purple-100 text-purple-700">CSV Upload</Badge>;
      case 'manual': return <Badge className="bg-slate-100 text-slate-700">Manual</Badge>;
      default: return <Badge variant="secondary">{source}</Badge>;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Dockets</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Equipment dockets - source of truth for actual work times</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button variant="outline" className="gap-2" asChild disabled={uploading}>
              <span>
                {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload CSV/Excel
              </span>
            </Button>
          </label>
          <Button onClick={() => { resetForm(); setIsCreateOpen(true); }} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Docket
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Dockets</p>
                <p className="text-2xl font-bold">{dockets.length}</p>
              </div>
              <FileSpreadsheet className="w-8 h-8 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">From EQUIPR</p>
                <p className="text-2xl font-bold">{dockets.filter(d => d.source === 'equipr').length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">CSV Uploads</p>
                <p className="text-2xl font-bold">{dockets.filter(d => d.source === 'csv_upload').length}</p>
              </div>
              <Upload className="w-8 h-8 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unique Employees</p>
                <p className="text-2xl font-bold">{new Set(dockets.map(d => d.employee_id)).size}</p>
              </div>
              <User className="w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search dockets..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterJob} onValueChange={setFilterJob}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map(job => (
                  <SelectItem key={job.id} value={job.id}>{job.job_number} - {job.job_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input 
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-40"
            />
            {filterDate && (
              <Button variant="ghost" size="sm" onClick={() => setFilterDate('')}>
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button variant="outline" onClick={fetchDockets} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dockets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Docket Records</CardTitle>
          <CardDescription>
            {filteredDockets.length} docket{filteredDockets.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredDockets.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No dockets found</p>
              <p className="text-sm text-slate-400 mt-1">Upload dockets via CSV or create manually</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Docket #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDockets.map((docket) => (
                  <TableRow key={docket.id}>
                    <TableCell className="font-mono text-sm">{docket.docket_number}</TableCell>
                    <TableCell>{docket.date}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{docket.employee_name}</p>
                        <p className="text-xs text-slate-500">{docket.employee_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{docket.job_number || 'N/A'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{docket.equipment_type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">{docket.actual_start_time}</TableCell>
                    <TableCell className="font-mono">{docket.actual_end_time}</TableCell>
                    <TableCell className="font-mono">
                      {calculateHours(docket.actual_start_time, docket.actual_end_time, docket.actual_break_minutes || 0)}h
                    </TableCell>
                    <TableCell>{getSourceBadge(docket.source)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => { setSelectedDocket(docket); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Docket?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete docket {docket.docket_number}.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(docket.id)} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Docket Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Docket Details</DialogTitle>
            <DialogDescription>Full docket information</DialogDescription>
          </DialogHeader>
          {selectedDocket && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-xs text-slate-500">Docket Number</Label>
                <p className="font-mono">{selectedDocket.docket_number}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Date</Label>
                <p>{selectedDocket.date}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Employee</Label>
                <p>{selectedDocket.employee_name} ({selectedDocket.employee_id})</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Job</Label>
                <p>{selectedDocket.job_number || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Equipment</Label>
                <p>{selectedDocket.equipment_type}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Registration</Label>
                <p>{selectedDocket.equipment_registration || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Start Time</Label>
                <p className="font-mono text-lg">{selectedDocket.actual_start_time}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">End Time</Label>
                <p className="font-mono text-lg">{selectedDocket.actual_end_time}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Break (mins)</Label>
                <p>{selectedDocket.actual_break_minutes || 0}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Total Hours</Label>
                <p className="font-mono text-lg font-bold">
                  {calculateHours(selectedDocket.actual_start_time, selectedDocket.actual_end_time, selectedDocket.actual_break_minutes || 0)}h
                </p>
              </div>
              {selectedDocket.work_description && (
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Work Description</Label>
                  <p className="text-sm">{selectedDocket.work_description}</p>
                </div>
              )}
              {selectedDocket.notes && (
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="text-sm">{selectedDocket.notes}</p>
                </div>
              )}
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Source</Label>
                <div className="mt-1">{getSourceBadge(selectedDocket.source)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Docket Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Create Docket</SheetTitle>
            <SheetDescription>Manually add a new docket record</SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Docket Number *</Label>
                  <Input 
                    placeholder="DOC-20241201-001"
                    value={formData.docket_number}
                    onChange={(e) => setFormData({...formData, docket_number: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input 
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Job</Label>
                <Select value={formData.job_id} onValueChange={(value) => setFormData({...formData, job_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>{job.job_number} - {job.job_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee ID *</Label>
                  <Input 
                    placeholder="EMP001"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee Name *</Label>
                  <Input 
                    placeholder="John Smith"
                    value={formData.employee_name}
                    onChange={(e) => setFormData({...formData, employee_name: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Equipment Type</Label>
                  <Input 
                    placeholder="Tower Crane CW7"
                    value={formData.equipment_type}
                    onChange={(e) => setFormData({...formData, equipment_type: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Registration</Label>
                  <Input 
                    placeholder="ABC123"
                    value={formData.equipment_registration}
                    onChange={(e) => setFormData({...formData, equipment_registration: e.target.value})}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">Times</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input 
                      type="time"
                      value={formData.actual_start_time}
                      onChange={(e) => setFormData({...formData, actual_start_time: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input 
                      type="time"
                      value={formData.actual_end_time}
                      onChange={(e) => setFormData({...formData, actual_end_time: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Break (mins)</Label>
                    <Input 
                      type="number"
                      value={formData.actual_break_minutes}
                      onChange={(e) => setFormData({...formData, actual_break_minutes: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Work Description</Label>
                <Textarea 
                  placeholder="Description of work performed..."
                  value={formData.work_description}
                  onChange={(e) => setFormData({...formData, work_description: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Supervisor</Label>
                <Input 
                  placeholder="Supervisor name"
                  value={formData.supervisor_name}
                  onChange={(e) => setFormData({...formData, supervisor_name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button className="flex-1" onClick={handleCreateDocket} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Docket
                </Button>
                <Button variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DocketsPage;
